import * as CertificationWatermark from '@tableau/certification-watermark';
import DesignTokens from '@tableau/design-tokens';
import {
  ButtonType, ButtonWidget,
  DialogPositioningBehavior,
  DialogProps, DialogType,
  DialogWidget,
  getClientPosition,
  InteractionState,
} from '@tableau/widgets';
import React from 'react';
import ReactDOM from 'react-dom';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~commonInfra/services/Telemetry';
import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { imgPath } from '~commonInfra/utils/ImgUrl';
import { HasClientLocation, mouseIsOutsideElement } from '~commonInfra/utils/RectangleUtils';
import { PageHeaderStyles } from '~src/spacesPlatform/components/layout/PageHeaderStyles';
import { filePathDisplayStringWithBaseNameFirst } from '~src/spacesPlatform/utils/FilePathUtils';
import { applyColor } from '~src/style/ColorService';

import { Messages } from '../../../../../codegen/Localize';
import { IDataSourceInfo } from '../../../../models/IDataSourceInfo';
import { isSameDataConnectionInfoProperties } from '../../../models/IDataConnectionInfo';

export interface PageHeaderDataConnectionButtonProps {
  readonly url: string;
  readonly getDataSourceUrl: (s: string) => string;
  readonly getDataSources: () => Promise<IDataSourceInfo[]>;
  readonly getVconnDataSourceUrl: (s: string) => string;
  readonly displayOnlyIcons: boolean;
}

export interface PageHeaderDataConnectionButtonState {
  readonly showingPanel: boolean;
  readonly isPanelLoading: boolean;
  readonly dataConnections: null | CertificationWatermark.DataConnection[];
}

export class PageHeaderDataConnectionButton extends React.PureComponent<PageHeaderDataConnectionButtonProps, PageHeaderDataConnectionButtonState> {

  private buttonElement: HTMLElement;
  private panelElement: HTMLElement;
  private canceled: boolean;
  private hasFetchedDataConnections: boolean;

  public constructor(props: PageHeaderDataConnectionButtonProps) {
    super(props);

    this.state = { showingPanel: false, isPanelLoading: false, dataConnections: [] };
    this.hasFetchedDataConnections = false;
  }

  public render(): JSX.Element {

    return (
      <div style={{ marginRight: this.props.displayOnlyIcons ? '-2px' : '-1px', lineHeight: PageHeaderStyles.headerHeight }}>
        <ButtonWidget
          ref={this.setButtonRef}
          buttonType={ButtonType.IconOutline}
          getContent={this.buttonContent}
          title={this.isPanelOpen() ? Messages.pageHeaderDatasourcesClose() : Messages.pageHeaderDatasourcesOpen()}
          testId={'page-header-dataconnections'}
          handleClick={this.handleButtonClick}
          style={{ cursor: 'pointer' }}
        />
        {this.state.showingPanel && !this.state.isPanelLoading && this.panel()}
      </div>
    );
  }

  private buttonContent = (interactionState: InteractionState): JSX.Element => {
    const showActiveIcon = (interactionState.active || interactionState.focus || interactionState.hover || interactionState.selected);
    const themeSuffix = '_dark';
    const iconPath = showActiveIcon ? `spaces_viz_view_header_ds_active${themeSuffix}.svg` : `spaces_viz_view_header_ds${themeSuffix}.svg`;
    const backgroundColor = { backgroundColor: (interactionState.hover ? applyColor(DesignTokens.color.component.background.neutral.default.hover, '#ebebeb') : 'transparent') };
    return (
      <span className={PageHeaderStyles.TextButton} style={backgroundColor}>
        <img src={imgPath(iconPath)}
          style={this.props.displayOnlyIcons
            ? PageHeaderStyles.iconOnlyButton
            : PageHeaderStyles.iconForTextButton}
          alt="" />
        {!this.props.displayOnlyIcons && Messages.pageHeaderDatasources()}
      </span>
    );
  };

  private setDataConnections = (dataConnections: CertificationWatermark.DataConnection[]) => {
    if (!this.canceled) {
      this.setState({ dataConnections });
    }
  };

  private toUniqueDataConnections = (dataSourceInfos: IDataSourceInfo[]): CertificationWatermark.DataConnection[] => {
    return dataSourceInfos.reduce(this.removeDuplicates, []).map(this.toDataConnection);
  };

  private toDataConnection = (dataSourceInfo: IDataSourceInfo): CertificationWatermark.DataConnection => {
    switch (dataSourceInfo.connectionDetails.type) {
      case 'file':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.name ||
            filePathDisplayStringWithBaseNameFirst(dataSourceInfo.connectionDetails.fileName),
          connectionType: 'File'
        };
      case 'tableauserver':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionDetails.name,
          connectionType: 'TableauServer',
          url: this.props.getDataSourceUrl(dataSourceInfo.connectionDetails.id),
          isCertified: dataSourceInfo.connectionDetails.certificationDetails.isCertified
        };
      case 'tableauserversite':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionDetails.siteUrl,
          connectionType: 'ExternalServer',
        };
      case 'virtualconnection':
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionDetails.name,
          connectionType: 'TableauServer',
          url: this.props.getVconnDataSourceUrl(dataSourceInfo.connectionDetails.id),
        };
      default:
        return {
          name: dataSourceInfo.name,
          displayName: dataSourceInfo.connectionTypeDisplayName,
          connectionType: 'ExternalServer',
          externalServer: dataSourceInfo.connectionDetails.serverName
        };
    }
  };

  private removeDuplicates(seen: IDataSourceInfo[], dataSourceInfo: IDataSourceInfo): IDataSourceInfo[] {
    return seen.some(isSameDataConnectionInfoProperties(dataSourceInfo)) ? seen : [...seen, dataSourceInfo];
  }

  public componentDidMount(): void {
    this.canceled = false;
  }

  // NOTE cancel any unresolved promise of content to avoid setting state on unmounted component,
  // see https://facebook.github.io/react/blog/2015/12/16/ismounted-antipattern.html for more information
  public componentWillUnmount(): void {
    this.canceled = true;
    this.stopWatchingClicks();
  }

  private setButtonRef = (element: ButtonWidget): void => {
    this.buttonElement = ReactDOM.findDOMNode(element) as HTMLElement;
  };

  private setPanelRef = (element: HTMLDivElement): void => {
    this.panelElement = ReactDOM.findDOMNode(element) as HTMLElement;
  };

  private handleButtonClick = () => {
    this.fetchDataConnections();
    this.togglePanelVisibility();
  };

  private togglePanelVisibility = () => {

    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'data-connections-tooltip',
      action: this.state.showingPanel ? 'close' : 'open'
    });

    this.setState((prevState) => {
      return { showingPanel: !prevState.showingPanel };
    }, () => {
      if (this.state.showingPanel) {
        this.startWatchingClicks();
      } else {
        this.stopWatchingClicks();
      }
    });
  };

  private fetchDataConnections = async () => {
    if (FeatureFlags.dataConnectionTooltipInVizView && !this.hasFetchedDataConnections) {
      this.setState({ isPanelLoading: true });
      const dataConnections = await this.props.getDataSources();
      const uniqueDataConnections = this.toUniqueDataConnections(dataConnections);
      this.setDataConnections(uniqueDataConnections);
      this.hasFetchedDataConnections = true;
      this.setState({ isPanelLoading: false });
    }
  };

  private hidePanel = () => {
    this.setState({ showingPanel: false });
    this.stopWatchingClicks();
  };

  private isPanelOpen() {
    return this.state.showingPanel;
  }

  private stopWatchingClicks = () => {
    document.removeEventListener('mousedown', this.onDocumentClick, true);
    document.removeEventListener('touchstart', this.onDocumentClick, true);
    document.removeEventListener('keyup', this.onKeyUp, true);
    window.removeEventListener('blur', this.onWindowBlur);
  };

  private startWatchingClicks = () => {
    document.addEventListener('mousedown', this.onDocumentClick, true);
    document.addEventListener('touchstart', this.onDocumentClick, true);
    document.addEventListener('keyup', this.onKeyUp, true);
    window.addEventListener('blur', this.onWindowBlur);
  };

  private onKeyUp = (evt: KeyboardEvent) => {
    if (evt.key === 'Escape' && this.buttonElement) {
      this.buttonElement.focus();
    }
  };

  private onDocumentClick = (evt: MouseEvent | TouchEvent) => {
    const clientPos = getClientPosition(evt);
    const hasClientLocation: HasClientLocation = {
      clientX: clientPos.x,
      clientY: clientPos.y
    };
    if (this.state.showingPanel && mouseIsOutsideElement(hasClientLocation, this.panelElement) && mouseIsOutsideElement(hasClientLocation, this.buttonElement)) {
      this.hidePanel();
    }
  };

  private onWindowBlur = () => {
    window.removeEventListener('blur', this.onWindowBlur);
    if (!this.canceled) {
      this.hidePanel();
    }
  };

  private panel = (): JSX.Element => {
    const props: DialogProps & React.HTMLAttributes<HTMLDivElement> = {
      anchorEl: this.buttonElement,
      testId: 'modal-dataconnections-pane',
      showCloseButton: false,
      type: DialogType.Uber,
      onCloseRequest: this.hidePanel,
      isModal: false,
      showFooter: false,
      shouldReturnFocusOnClose: true,
      positioningBehavior: DialogPositioningBehavior.Anchored,
      disableAutoFocus: false,
      dialogStyle: { padding: 0, boxShadow: 'none', borderWidth: '0px' },
      dialogContentStyle: {
        padding: 0,
        borderWidth: CommonStyles.Sizes.LineWidth,
        borderColor: applyColor(DesignTokens.color.elevation.border.default.default, CommonStyles.Colors.BorderColor),
        borderStyle: 'solid'
      },
      title: Messages.dataConnectionDialogTitle(),
      dialogBodyStyle: { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }
    };

    const contentProps: CertificationWatermark.DataConnectionTooltipProps = {
      label: Messages.pageHeaderDatasources(),
      url: this.props.url,
      dataConnections: this.state.dataConnections
    };

    return (
      <DialogWidget {...props}>
        <div ref={this.setPanelRef} className={PageHeaderStyles.dataConnectionsTooltipClass}>
          <CertificationWatermark.DataConnectionTooltipComponentContent {...contentProps} />
        </div>
      </DialogWidget>
    );
  };
}
